// The cards of a room's panel: its light levels (and the lux overlay), the
// lighting copied from one room to another, its colours, its woods, a steam
// room's finishes, its bench layout, and a product's own properties card.
// docs/claude/lights.md, docs/claude/woods.md.
import {
  FINISH_IDS,
  FINISHES,
  finishOf,
  NO_FINISH,
  roomFinishOf,
  setRoomFinish,
  takesFinish,
} from '../../shared/finishes';
import {
  type ProductInstance,
  type RoomFinishes,
  type RoomWoods,
} from '../../shared/types';
import {
  ceilingSlatsOf,
  setRoomWood,
  takesWood,
  wallSlatsOf,
  WOOD_IDS,
  woodOf,
  WOODS,
} from '../../shared/woods';
import {
  benchCapacity,
  benchSeatingLength,
} from '../benches';
import {
  ceilingColorOf,
  ceilingCuts,
  hasCeiling,
  ownCeilingColor,
  setCeilingColor,
} from '../ceiling';
import {
  flashStatus,
  instLocked,
  invalidate,
  newId,
  objHidden,
  pushHistory,
  renderProperties,
  state,
} from './core';
import {
  wallThicknessOf,
  wallThicknessRange,
} from '../dent';
import {
  clampDoor,
  defaultDoor,
  flipDoor,
  roomDoors,
} from '../door';
import {
  doorGlassSegments,
  mergeGlassSegments,
} from '../glass';
import { doorTop } from '../heights';
import { tr } from '../i18n';
import { isFibreKit } from '../lightLife';
import { setKitProjector } from './lightTools';
import {
  adaptLights,
  BEAM_DEFAULTS,
  type LightLevels,
  lightLevels,
  type LuxStats,
  projectorHome,
} from '../lights';
import {
  buttonRow,
  colorRow,
  deleteRow,
  infoRow,
  noteRow,
  numberRow,
  sectionTitle,
  selectRow,
  showColorRow,
} from './panelRows';
import {
  doorRun,
  isPolyRoom,
  polyBenchCapacity,
  polyBenchSeatingLength,
  segToWall,
  wallRuns,
} from '../polyRoom';
import {
  defaultWallColor,
  type RenderOptions,
} from '../render';
import { dropFeature } from '../selection';
import {
  $,
  editRoom,
  glassPanesOf,
  paneOnWall,
  persistViewPrefs,
  pickDoor,
  pickSegments,
  polyBBox,
  propRow,
  regenerateSauna,
  saunaHeaterHoles,
  selDoorId,
  selGlassId,
} from '../main';
import { createLightingSheet } from './sheetStrip';
import { appendGlazingCard } from './glazingCard';
import { roomDoor } from '../roomDoor';

// ---- Light levels: each room's lux over its floor and benches ----------------------
/** A room's light levels under the drawing's beam sizes (lights.ts resolves
 *  them once, until the room or its lights' products change). */
function luxOf(inst: ProductInstance): LightLevels | null {
  if (!inst.lights?.length || !isPolyRoom(inst)) return null;
  return lightLevels(inst, 100, state.drawing.lightBeams ?? BEAM_DEFAULTS);
}
/** Every painted room's light levels, while the toggle is on. */
export function luxOverlay(): RenderOptions['lux'] {
  if (!state.showLux || state.disciplineFilters.lighting === false) return null;
  const maps = state.drawing.instances.filter((i) => i.autoBenches && !objHidden(i.id)).flatMap((i) => {
    const lv = luxOf(i);
    return lv ? [{ x: i.x, y: i.y, rotation: i.rotation, cell: lv.cell, cells: lv.cells }] : [];
  });
  return { title: tr('Light levels (lx)'), maps };
}
export function toggleLux(): void {
  state.showLux = !state.showLux;
  $('btn-lux').classList.toggle('active', state.showLux);
  persistViewPrefs();
  renderProperties();
  invalidate();
}
/** A room's light levels card: on the floor and on the benches, the mean, least
 *  and most lux; the button that shows them over the plan. */
/** The room whose lighting Copy lighting took — a copy of it as it was then. */
let lightingClipboard: ProductInstance | null = null;

/** A walled room's Lighting card: Copy lighting and — another room's taken —
 *  Paste lighting; New lighting plan sheet. */
export function appendLightingCopyCard(body: HTMLElement, inst: ProductInstance): void {
  if (!inst.autoBenches || !isPolyRoom(inst)) return;
  const has = !!inst.lights?.length, canPaste = !!lightingClipboard && lightingClipboard.id !== inst.id && !instLocked(inst);
  if (!has && !canPaste) return;
  body.appendChild(sectionTitle('Lighting'));
  if (has) {
    body.appendChild(buttonRow('Copy lighting', () => {
      lightingClipboard = JSON.parse(JSON.stringify(inst)) as ProductInstance;
      flashStatus(tr('Lighting copied: select another room and paste it there.'));
    }));
  }
  if (canPaste) body.appendChild(buttonRow('Paste lighting', () => pasteLighting(inst)));
  if (has) body.appendChild(buttonRow('New lighting plan sheet', () => createLightingSheet(inst)));
}

/** The lighting copied put in a room, adapted to its walls (`adaptLights`), one
 *  undo step; a fibre kit gets a projector over the room; what finds no place
 *  there is reported. */
function pasteLighting(room: ProductInstance): void {
  const src = lightingClipboard;
  if (!src) return;
  const { lights, skipped } = adaptLights(src, room, () => newId('lt'));
  if (!lights.length) { flashStatus(tr('None of the copied lights finds a place in this room.')); return; }
  pushHistory();
  room.lights = [...(room.lights ?? []), ...lights];
  for (const l of lights) if (isFibreKit(l)) setKitProjector(room, l, projectorHome(room, l));
  const msg = skipped.length ? tr('{n} lights pasted; {m} found no place in this room.') : tr('{n} lights pasted.');
  flashStatus(msg.replace('{n}', String(lights.length)).replace('{m}', String(skipped.length)));
  renderProperties();
  invalidate();
}

export function appendLightLevelsCard(body: HTMLElement, inst: ProductInstance): void {
  const lv = luxOf(inst);
  if (!lv) return;
  const figures = (s: LuxStats): string => tr('{avg} lx average · {min}–{max} lx').replace('{avg}', String(Math.round(s.avg))).replace('{min}', String(Math.round(s.min))).replace('{max}', String(Math.round(s.max)));
  body.appendChild(sectionTitle('Light levels'));
  if (lv.floor) body.appendChild(infoRow('On the floor', figures(lv.floor)));
  if (lv.benches) body.appendChild(infoRow('On the benches', figures(lv.benches)));
  body.appendChild(buttonRow(state.showLux ? 'Hide the light levels' : 'Show the light levels', () => toggleLux()));
  const hint = document.createElement('div');
  hint.className = 'prop-empty';
  hint.textContent = tr('Direct light from the products’ lumens and beam angles: no shadows, nothing reflected off the walls.');
  body.appendChild(hint);
}

export function appendRoomColoursCard(body: HTMLElement, inst: ProductInstance): void {
  if (inst.autoBenches) {
    body.appendChild(sectionTitle('Colours'));
    // While the ceiling follows the walls its row shows the colour it follows
    // them in (theirs), so a Walls edit
    // updates it too (colorRow does not re-render the panel).
    let ceilRow: HTMLElement | null = null;
    const followed = (): string => ceilingColorOf(inst, inst.wallColor || defaultWallColor(inst));
    body.appendChild(colorRow('Walls', inst.wallColor || defaultWallColor(inst), (hex) => {
      inst.wallColor = hex;
      if (ceilRow && ownCeilingColor(inst) == null) showColorRow(ceilRow, followed());
    }));
    if (hasCeiling(inst)) {
      body.appendChild(ceilRow = colorRow('Ceiling', followed(), (hex) => setCeilingColor(inst, hex), {
        // An emptied field follows the walls again.
        onClear: () => { setCeilingColor(inst, null); return followed(); },
      }));
    }
  }
}

/** Every wood, as a row's options (their names are translated by the row), and
 *  the option for none — not '', which a multi-selection's "(mixed)" is. */
export const WOOD_OPTIONS = WOOD_IDS.map((id) => [id, WOODS[id].name] as const);
export const NO_WOOD = 'none';
export const WALL_SLAT_OPTIONS = [['horizontal', 'Horizontal'], ['vertical', 'Vertical']] as const;
/** A ceiling's boards: along its longest wall, or across it. */
export const CEILING_SLAT_OPTIONS = [['lengthwise', 'Lengthwise'], ['crosswise', 'Crosswise']] as const;

/** The woods a sauna is clad in, in 3D (docs/claude/woods.md): its walls inside
 *  and out, its ceiling and its benches, each but the benches with the way its
 *  boards run once it has a wood. Each change is one undo step. */
export function appendRoomWoodCard(body: HTMLElement, inst: ProductInstance): void {
  if (!takesWood(inst)) return;
  body.appendChild(sectionTitle('Wood'));
  const edit = (part: keyof RoomWoods, change: { wood?: string; slats?: string }): void => {
    pushHistory();
    setRoomWood(inst, part, change);
    renderProperties();
    invalidate();
  };
  const has = (part: keyof RoomWoods): boolean => !!woodOf(inst.woods?.[part]?.wood);
  const wood = (label: string, part: keyof RoomWoods, none: string): void => {
    body.appendChild(selectRow(label, [[NO_WOOD, none], ...WOOD_OPTIONS], woodOf(inst.woods?.[part]?.wood)?.id ?? NO_WOOD, (v) => edit(part, { wood: v === NO_WOOD ? '' : v })));
  };
  wood('Walls inside', 'wallsIn', 'Colour (no wood)');
  if (has('wallsIn')) body.appendChild(selectRow('Slats inside', WALL_SLAT_OPTIONS, wallSlatsOf(inst.woods, 'in'), (v) => edit('wallsIn', { slats: v })));
  wood('Walls outside', 'wallsOut', 'As inside');
  if (has('wallsOut')) body.appendChild(selectRow('Slats outside', WALL_SLAT_OPTIONS, wallSlatsOf(inst.woods, 'out'), (v) => edit('wallsOut', { slats: v })));
  if (hasCeiling(inst)) {
    wood('Ceiling', 'ceiling', 'As the walls inside');
    if (has('ceiling')) body.appendChild(selectRow('Ceiling slats', CEILING_SLAT_OPTIONS, ceilingSlatsOf(inst.woods), (v) => edit('ceiling', { slats: v })));
  }
  wood('Benches', 'benches', 'Colour (no wood)');
}

/** Every steam room finish and the colour, as a row's options (their names are
 *  translated by the row), and the ceiling's option to follow the walls inside
 *  — not '', which a multi-selection's "(mixed)" is. */
export const FINISH_OPTIONS = [...FINISH_IDS.map((id) => [id, FINISHES[id].name] as const), [NO_FINISH, 'Colour (no texture)'] as const];
export const CEILING_FINISH_OPTIONS = [['walls', 'As the walls inside'] as const, ...FINISH_OPTIONS];

/** A room's ceiling finish as its row shows it: its own, or following the walls. */
export function ceilingFinishValue(f: RoomFinishes | undefined): string {
  const v = f?.ceiling;
  return v && (v === NO_FINISH || finishOf(v)) ? v : 'walls';
}

/** A steam room's finishes in 3D (docs/claude/woods.md, *Steam room finishes*):
 *  its walls inside and out and its ceiling, each a finish or its colour. Each
 *  change is one undo step. */
export function appendRoomFinishCard(body: HTMLElement, inst: ProductInstance): void {
  if (!takesFinish(inst)) return;
  body.appendChild(sectionTitle('Finish'));
  const edit = (part: keyof RoomFinishes, v: string): void => {
    pushHistory();
    setRoomFinish(inst, part, v === 'walls' ? '' : v);
    renderProperties();
    invalidate();
  };
  body.appendChild(selectRow('Walls inside', FINISH_OPTIONS, roomFinishOf(inst.finishes, 'wallsIn') ?? NO_FINISH, (v) => edit('wallsIn', v)));
  body.appendChild(selectRow('Walls outside', FINISH_OPTIONS, roomFinishOf(inst.finishes, 'wallsOut') ?? NO_FINISH, (v) => edit('wallsOut', v)));
  if (hasCeiling(inst)) body.appendChild(selectRow('Ceiling', CEILING_FINISH_OPTIONS, ceilingFinishValue(inst.finishes), (v) => edit('ceiling', v)));
}

export function appendRoomBenchLayoutCard(body: HTMLElement, inst: ProductInstance): void {
  if (inst.autoBenches) {
    body.appendChild(sectionTitle('Bench layout'));
    const wallRange = wallThicknessRange(inst.category);
    body.appendChild(numberRow('Wall thickness (mm)', wallThicknessOf(inst), (v) => {
      editRoom(inst, () => {
        inst.wallThickness = Math.max(wallRange.min, Math.min(wallRange.max, Math.round(v)));
        if (!isPolyRoom(inst)) for (const d of roomDoors(inst)) if (d.innerWallId == null) clampDoor(inst, d);
      });
    }, { commit: true }));

    // Seating + capacity from the actual bench runs: one person per 60 cm, floored
    // per run. Rectangular rooms split runs at dents / the feature cube / grouped
    // heaters; polygonal rooms sum each bench's shorter long side (the wall-side and
    // room-side edges differ when the ends are mitred at a corner).
    const holes = saunaHeaterHoles(inst); // heaters (sauna) or feature cube (steam) carve
    // The legacy rectangular run-length maths is box-based; poly rooms (i.e. every
    // room in use) ignore `holes` entirely and measure their own bands.
    const holeBoxes = holes.map(polyBBox);
    const seatMm = isPolyRoom(inst) ? polyBenchSeatingLength(inst) : benchSeatingLength(inst, holeBoxes);
    const cap = isPolyRoom(inst) ? polyBenchCapacity(inst) : benchCapacity(inst, holeBoxes);
    body.appendChild(infoRow('Seating', seatMm ? `${(seatMm / 1000).toFixed(2)} m` : '—'));
    body.appendChild(infoRow('Capacity', tr(cap === 1 ? '{n} person' : '{n} persons').replace('{n}', String(cap))));

    // Doors (opens outwards) — a room may carry several, on outer walls or
    // interior partitions. Click a door here (or on the plan) to edit it.
    body.appendChild(sectionTitle('Doors (open outwards)'));
    const doors = roomDoors(inst);
    body.appendChild(infoRow('Doors', `${doors.length}`));
    doors.forEach((d, i) => {
      const dw = d.innerWallId != null ? 0 : segToWall(inst, d.edge ?? 0)?.wall ?? d.edge ?? 0;
      const where = d.innerWallId != null ? 'inner wall' : dw === 0 ? 'front wall' : `wall ${dw + 1}`;
      const isSel = d.id === selDoorId();
      body.appendChild(buttonRow(`${isSel ? '● ' : ''}${tr('Door')} ${i + 1} — ${where}`, () => {
        pickDoor(d.id);
        renderProperties();
        invalidate();
      }));
    });
    body.appendChild(buttonRow('＋ Add door (front wall)', () => {
      pushHistory();
      const run0 = doorRun(inst, { id: '', offset: 0, hinge: 'left', width: 1, edge: 0 });
      const nd = defaultDoor(run0?.len ?? inst.width, { edge: 0 }, inst.category);
      roomDoors(inst).push(nd);
      pickDoor(nd.id);
      regenerateSauna(inst);
      renderProperties();
      invalidate();
    }));
    const selD = doors.find((d) => d.id === selDoorId());
    if (selD) {
      const rd = roomDoor(inst, selD);
      const len = rd?.run.len ?? 0;
      const span = rd?.span ?? { start: 0, end: selD.width };
      // An outer-wall door sits inside one plain segment of its wall, but its
      // distances read from the wall's CORNERS, not from a pane's jamb.
      const onWall = selD.innerWallId == null ? segToWall(inst, selD.edge ?? 0) : null;
      const off = onWall?.offset ?? 0;
      const wallLen = onWall ? wallRuns(inst)[onWall.wall].len : len;
      const leftGap = off + span.start;
      const rightGap = Math.max(0, wallLen - off - span.end);
      const setAlong = (newMid: number): void => {
        selD.offset = Math.max(selD.width / 2, Math.min(Math.max(selD.width / 2, len - selD.width / 2), newMid)) - len / 2;
      };
      body.appendChild(infoRow('Hinge', tr(selD.hinge === 'left' ? 'Left' : 'Right')));
      body.appendChild(buttonRow('Flip hinge (left ⇄ right)', () => {
        pushHistory(); flipDoor(selD); regenerateSauna(inst); renderProperties(); invalidate();
      }));
      // A door on a partition has interior on BOTH sides, so it can also be flipped
      // push ⇄ pull. An outer-wall door always opens into the room.
      if (selD.innerWallId != null) {
        body.appendChild(infoRow('Opening', tr(selD.swingBack ? 'Push' : 'Pull')));
        body.appendChild(buttonRow('Flip opening (push ⇅ pull)', () => {
          pushHistory(); selD.swingBack = !selD.swingBack; regenerateSauna(inst); renderProperties(); invalidate();
        }));
      }
      body.appendChild(numberRow('Width (mm)', selD.width, (v) => {
        pushHistory();
        selD.width = Math.max(300, len > 0 ? Math.min(len, v) : v);
        regenerateSauna(inst); renderProperties(); invalidate();
      }, { commit: true }));
      // Its own height, else the room's Glass height (heights.ts doorTop: the one
      // resolver section, 3D and the cut note read).
      body.appendChild(numberRow('Height (mm)', Math.round(doorTop(inst, selD)), (v) => {
        const h = Math.max(1, Math.round(v));
        if (h === Math.round(doorTop(inst, selD))) return;
        pushHistory(); selD.height = h; renderProperties(); invalidate();
      }, { commit: true }));
      if (selD.height != null) {
        const reset = document.createElement('button');
        reset.className = 'link-btn';
        reset.textContent = tr('Use default height');
        reset.addEventListener('click', () => { pushHistory(); delete selD.height; renderProperties(); invalidate(); });
        body.appendChild(reset);
      }
      if (selD.id && ceilingCuts(inst).has(selD.id)) body.appendChild(noteRow('Cut by the ceiling'));
      body.appendChild(numberRow('Distance from left (mm)', Math.round(leftGap), (v) => {
        pushHistory(); setAlong(v - off + selD.width / 2); regenerateSauna(inst); renderProperties(); invalidate();
      }, { commit: true }));
      body.appendChild(numberRow('Distance from right (mm)', Math.round(rightGap), (v) => {
        pushHistory(); setAlong(wallLen - v - off - selD.width / 2); regenerateSauna(inst); renderProperties(); invalidate();
      }, { commit: true }));
      const gz = rd?.glazing ?? null;
      if (gz) appendGlazingCard(body, inst, gz);
      body.appendChild(deleteRow(() => {
        pushHistory();
        inst.doors = roomDoors(inst).filter((d) => d.id !== selD.id);
        dropFeature(state, 'door', 'glass');
        regenerateSauna(inst); renderProperties(); invalidate();
      }, 'Delete door')); // the room's panel: say it is the door that goes
      const hint = document.createElement('div');
      hint.className = 'prop-empty';
      hint.textContent = tr('Tip: drag the door on the plan to move it, or click it to flip. Use the Door tool to add one to any wall.');
      body.appendChild(hint);
    }

    // Glass walls.
    body.appendChild(sectionTitle('Glass walls'));
    const glassCount = (inst.glass || []).length;
    const doorSegs = doorGlassSegments(inst);
    body.appendChild(infoRow('Glazed panes', `${glassCount} (+ ${tr(doorSegs.length === 1 ? '{n} door' : '{n} doors').replace('{n}', String(doorSegs.length))})`));
    const glassH = inst.glassHeight ?? 2000;
    const glassLen = mergeGlassSegments([...(inst.glass || []), ...doorSegs])
      .reduce((sum, g) => sum + (g.end - g.start), 0);
    body.appendChild(infoRow('Glass area', `${(glassLen * glassH / 1e6).toFixed(2)} m²`));
    body.appendChild(numberRow('Glass height (mm)', glassH, (v) => {
      pushHistory();
      inst.glassHeight = Math.max(1, v);
      renderProperties();
      invalidate();
    }, { commit: true }));
    // One row per pane, exactly like the doors list: click it here (or click the
    // pane itself on the plan) to sub-select it and edit its own colour and size.
    const panes = glassPanesOf(inst);
    panes.forEach((g, i) => {
      const where = g.innerWallId != null ? tr('inner wall') : `${tr('wall')} ${(paneOnWall(inst, g)?.w ?? g.edge ?? 0) + 1}`;
      const isSel = !!g.id && g.id === selGlassId();
      body.appendChild(buttonRow(`${isSel ? '● ' : ''}${tr('Pane')} ${i + 1} — ${where}`, () => {
        pickSegments('glass', [g.id]);
        renderProperties();
        invalidate();
      }));
    });
    // A pane's own size / height / height-from-ground / colour live in the dedicated
    // pane panel (renderGlassPanePanel), shown INSTEAD of this room panel once a pane
    // is sub-selected — so nothing pane-specific is appended here.
    if (glassCount) {
      body.appendChild(buttonRow('Clear glass panes', () => {
        pushHistory();
        inst.glass = [];
        dropFeature(state, 'glass');
        renderProperties();
        invalidate();
      }));
    }

    // Openings (glass-less): the wall is drawn at 50% opacity over the opening.
    body.appendChild(sectionTitle('Openings'));
    const openCount = (inst.openings || []).length;
    body.appendChild(infoRow('Openings', `${openCount}`));
    if (openCount) {
      body.appendChild(buttonRow('Clear openings', () => {
        pushHistory();
        inst.openings = [];
        renderProperties();
        invalidate();
      }));
    }

    // Inner walls: straight interior partitions perpendicular to their wall.
    body.appendChild(sectionTitle('Inner walls'));
    const innerCount = (inst.innerWalls || []).length;
    body.appendChild(infoRow('Inner walls', `${innerCount}`));
    if (innerCount) {
      body.appendChild(buttonRow('Clear inner walls', () => {
        pushHistory();
        inst.innerWalls = [];
        renderProperties();
        invalidate();
      }));
    }

  }
}

export function appendProductPropertiesCard(body: HTMLElement, inst: ProductInstance): void {
  if (!inst.autoBenches) {
    body.appendChild(sectionTitle('Product properties'));
    if (inst.properties.length === 0) {
      const e = document.createElement('div');
      e.className = 'prop-empty';
      e.textContent = tr(inst.groupId ? 'This part has no properties.' : 'This product has no properties.');
      body.appendChild(e);
    }
    inst.properties.forEach((prop) => {
      body.appendChild(propRow(prop));
    });
  }
}
